import {addUrlToHistoryStack} from '@github-ui/history'
import type {IssueReference} from '@github-ui/issue-viewer/Types'
import {ssrSafeLocation} from '@github-ui/ssr-utils'

/** Enough to identify the issue; callers add whatever else their own identifier type carries. */
export type SidePanelIssue = IssueReference & {globalId?: string}

/** The side panel's URL contract: `?issue=owner|repo|number`. */
export const SIDE_PANEL_SEARCH_PARAM = 'issue'

// Dot-only segments are path traversal once interpolated: `/o/../issues/1` is `/issues/1`.
const NAME_SEGMENT = /^(?!\.+$)[\w.-]+$/
const REPOSITORY_ISSUE_REFERENCE = /^([^|]+)\|([^|]+)\|(\d+)$/

export function parseSidePanelIssue(href: string = ssrSafeLocation.href): SidePanelIssue | null {
  const url = new URL(href, ssrSafeLocation.origin)
  const reference = url.searchParams.get(SIDE_PANEL_SEARCH_PARAM)
  if (!reference) return null

  const repositoryReferenceMatch = REPOSITORY_ISSUE_REFERENCE.exec(reference)
  const [, owner, repo, rawNumber] = repositoryReferenceMatch ?? []
  // Interpolated into hrefs, so `//evil.com` as an owner would resolve off-site.
  if (!owner || !repo || !NAME_SEGMENT.test(owner) || !NAME_SEGMENT.test(repo)) return null

  const number = Number(rawNumber)
  // Rejects `0`, negatives and `12abc`, all of which `parseInt` would have accepted in some form.
  if (!Number.isSafeInteger(number) || number < 1) return null

  return {owner, repo, number}
}

export function serializeSidePanelIssue(issue: SidePanelIssue): string | null {
  return issue.owner && issue.repo ? `${issue.owner}|${issue.repo}|${issue.number}` : null
}

/** `pushState`, not a router navigation: the panel is not a route, and navigating would refetch. */
export function writeSidePanelIssueToUrl(issue: SidePanelIssue | null): void {
  const url = new URL(ssrSafeLocation.href, ssrSafeLocation.origin)
  const reference = issue ? serializeSidePanelIssue(issue) : null
  if (reference) {
    url.searchParams.set(SIDE_PANEL_SEARCH_PARAM, reference)
  } else {
    url.searchParams.delete(SIDE_PANEL_SEARCH_PARAM)
  }
  addUrlToHistoryStack(url.toString())
}
